/**
 * 合同与事后抽查（法务岗）
 *
 * 法务在平台上有两件事：
 *   1. 维护**合同台账** —— 校验报告里的「合同比对」全靠这份数据，合同录错会让比对结果失真；
 *   2. **事后抽查** —— 从已记账/已归档的发票里抽样复盘，看校验报告、人工修正、特例放行是否合规。
 *
 * 抽查是这套系统「自动化之后仍然有人负责」的最后一环，因此抽查结果支持标记异常并记录结论。
 */

import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Progress,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  AuditOutlined,
  FileSearchOutlined,
  PlusOutlined,
  SafetyCertificateOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import {
  INVOICE_STATUS_LABELS,
  InvoiceStatus,
  PermissionCode,
  RISK_LEVEL_LABELS,
  RiskLevel,
  formatCents,
  type Contract,
  type ContractAttachment,
  type InvoiceListItem,
} from '@ilc/shared';
import { archiveApi, contractApi, invoiceApi } from '@/api/endpoints';
import { mockArchiveList, mockContractList, mockInvoiceList } from '@/mocks';
import { usePagedList } from '@/hooks/usePagedList';
import { useResource } from '@/hooks/useResource';
import { useAuthStore } from '@/stores/auth';
import { DataTable } from '@/components/DataTable';
import { AttachmentStatusTag } from '@/components/AttachmentStatusTag';
import {
  ContractAttachmentIntake,
  type ContractAttachmentIntakeHandle,
} from '@/components/ContractAttachmentIntake';
import { TablePreview } from '@/components/TablePreview';
import { LayoutPreview } from '@/components/LayoutPreview';
import { Drawer } from 'antd';
import dayjs from 'dayjs';
import { PageHeader } from '@/components/PageHeader';
import { PageState } from '@/components/PageState';
import { RiskTag } from '@/components/RiskTag';
import { InvoiceStatusTag } from '@/components/InvoiceStatusTag';
import { Money } from '@/components/Money';
import { buildInvoiceColumns, buildInvoiceExportColumns } from '@/components/invoiceColumns';
import { performAction } from '@/utils/performAction';
import { feedback } from '@/utils/feedback';
import { exportCsv } from '@/utils/export';

interface ContractQuery {
  page?: number;
  pageSize?: number;
  keyword?: string;
  status?: string;
}

interface SampleQuery {
  page?: number;
  pageSize?: number;
  keyword?: string;
  riskLevel?: RiskLevel;
}

/** 合同台账 */
function ContractTab(): React.ReactElement {
  const canWrite = useAuthStore((s) => s.can)(PermissionCode.CONTRACT_WRITE);
  const [editing, setEditing] = useState<Contract | null>(null);
  /** 本次上传但尚未绑定到合同的附件（TEMP 态），保存合同时一并带上 */
  const [tempAttachmentIds, setTempAttachmentIds] = useState<string[]>([]);
  /** 已上传并解析完的附件（用于展示状态、原件与表格） */
  const [uploaded, setUploaded] = useState<ContractAttachment[]>([]);
  /** 上传后识别到的回填候选，用于展示"依据"供人工核对 */
  /**
   * 收件组件的句柄：附件抽屉里的「重新提取」要用它刷新识别面板。
   *
   * 【为什么必须留着这条通路】重新提取后界面若无变化，用户会以为没生效，
   * 而数据其实已经变了——那是静默的不一致。面板搬进组件之后，
   * 调用方够不着它，所以由组件开放 `refreshFrom`。
   */
  const intakeRef = useRef<ContractAttachmentIntakeHandle>(null);
  /** 附件详情抽屉 */
  const [detailAtt, setDetailAtt] = useState<ContractAttachment | null>(null);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  /**
   * 表单初值。
   *
   * 【为什么用 initialValues 而不是 setFieldsValue】
   * Modal 带 destroyOnHidden，每次打开都会重新挂载 Form，
   * 挂载瞬间按 initialValues 初始化。用命令式赋值要么早于挂载被丢弃
   * （编辑弹窗全空），要么晚于挂载造成闪烁。交给 Form 自己按 key 初始化最稳。
   *
   * key 取合同 id：从"编辑 A"切到"编辑 B"、或切到"新增"时，
   * Form 会整体重建，不会有上一条合同的残留值。
   */
  const formInitialValues = useMemo(
    () =>
      editing
        ? {
            contractNo: editing.contractNo,
            name: editing.name,
            supplierName: editing.supplierName,
            supplierTaxNo: editing.supplierTaxNo ?? '',
            amountYuan: editing.amountCents / 100,
            // signDate 是字符串，DatePicker 要的是 dayjs 对象
            signDate: editing.signDate ? dayjs(editing.signDate) : null,
            settlementStage: editing.settlementStage ?? '',
            categories: editing.categories?.join('、') ?? '',
            remark: editing.remark ?? '',
          }
        : {},
    [editing],
  );

  const list = usePagedList<Contract, ContractQuery>({
    loader: (q) => contractApi.list(q),
    fallback: (q) => mockContractList(q),
    initialQuery: { page: 1, pageSize: 20 },
  });

  const save = async (): Promise<void> => {
    const values = await form.validateFields();
    setSaving(true);
    const payload = {
      contractNo: String(values.contractNo ?? ''),
      name: String(values.name ?? ''),
      supplierName: String(values.supplierName ?? ''),
      supplierTaxNo: values.supplierTaxNo ? String(values.supplierTaxNo) : null,
      amountCents: Math.round(Number(values.amountYuan ?? 0) * 100),
      settlementStage: values.settlementStage ? String(values.settlementStage) : null,
      categories: values.categories ? String(values.categories).split(/[、,，]/).filter(Boolean) : null,
      remark: values.remark ? String(values.remark) : null,
      // sign_date 是 date 列，dayjs 对象会被后端的 z.string() 拒掉，必须先格式化
      signDate: values.signDate ? dayjs(values.signDate).format('YYYY-MM-DD') : null,
      // 把本次上传但还没归属的附件一并绑定；没有则为 undefined（不传）
      ...(tempAttachmentIds.length > 0 ? { attachmentIds: tempAttachmentIds } : {}),
    };
    const result = await performAction({
      action: () =>
        editing ? contractApi.update(editing.id, payload) : contractApi.create(payload),
      successMessage: editing ? '合同已更新' : '合同已创建',
      offline: () => undefined,
      offlineMessage: '后端合同接口尚未就绪，本次保存仅在本地演示，未写入数据库',
    });
    setSaving(false);
    if (result.ok) {
      setEditing(null);
      setCreating(false);
      form.resetFields();
      setTempAttachmentIds([]);
      list.reload();
    }
  };

  const totalAmount = list.list.reduce((sum, c) => sum + c.amountCents, 0);
  const totalInvoiced = list.list.reduce((sum, c) => sum + c.invoicedCents, 0);

  return (
    <>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="合同台账是「合同比对」的数据源"
        description="校验报告会拿发票金额与合同金额、结算阶段、约定品类做比对。合同金额录错会导致「超出合同额度」误报，约定品类缺失则无法判断是否超经营范围。建议法务在合同签订后及时录入。"
      />

      <Row gutter={[16, 16]} style={{ marginBottom: 12 }}>
        <Col xs={12} lg={6}>
          <Card size="small">
            <Statistic title="本页合同数" value={list.list.length} suffix="份" />
          </Card>
        </Col>
        <Col xs={12} lg={6}>
          <Card size="small">
            <Statistic title="合同金额合计" value={formatCents(totalAmount)} prefix="¥" />
          </Card>
        </Col>
        <Col xs={12} lg={6}>
          <Card size="small">
            <Statistic title="已开票金额合计" value={formatCents(totalInvoiced)} prefix="¥" />
          </Card>
        </Col>
        <Col xs={12} lg={6}>
          <Card size="small">
            <Statistic
              title="剩余可开票额度"
              value={formatCents(Math.max(0, totalAmount - totalInvoiced))}
              prefix="¥"
              valueStyle={{ color: '#389e0d' }}
            />
          </Card>
        </Col>
      </Row>

      <DataTable<Contract>
        storageKey="legal-contracts"
        exportFileName="合同台账"
        exportColumns={[
          { title: '合同编号', dataIndex: 'contractNo' },
          { title: '合同名称', dataIndex: 'name' },
          { title: '供应商', dataIndex: 'supplierName' },
          { title: '合同金额(元)', value: (r) => formatCents(r.amountCents) },
          { title: '已开票(元)', value: (r) => formatCents(r.invoicedCents) },
          { title: '签订日期', dataIndex: 'signDate' },
          { title: '结算阶段', dataIndex: 'settlementStage' },
          { title: '状态', dataIndex: 'status' },
        ]}
        columns={[
          {
            title: '合同编号',
            dataIndex: 'contractNo',
            width: 160,
            fixed: 'left',
            render: (v: string) => (
              <span className="ilc-mono" style={{ fontSize: 12 }}>
                {v}
              </span>
            ),
          },
          { title: '合同名称', dataIndex: 'name', width: 220, ellipsis: true },
          { title: '供应商', dataIndex: 'supplierName', width: 220, ellipsis: true },
          {
            title: '合同金额',
            dataIndex: 'amountCents',
            width: 150,
            align: 'right',
            render: (v: number) => <Money cents={v} />,
          },
          {
            title: '已开票',
            dataIndex: 'invoicedCents',
            width: 150,
            align: 'right',
            render: (v: number, record) => (
              <Space direction="vertical" size={2} style={{ width: '100%' }}>
                <Money cents={v} />
                <Progress
                  percent={
                    record.amountCents === 0 ? 0 : Math.min(100, Math.round((v / record.amountCents) * 100))
                  }
                  size="small"
                  status={v > record.amountCents ? 'exception' : 'normal'}
                  showInfo={false}
                />
              </Space>
            ),
          },
          {
            title: '剩余额度',
            key: 'remain',
            width: 150,
            align: 'right',
            render: (_v, record) => {
              const remain = record.amountCents - record.invoicedCents;
              return remain < 0 ? (
                <Tooltip title="已开票金额超过合同金额，需要核实是否补充协议">
                  <span>
                    <Money cents={remain} />
                  </span>
                </Tooltip>
              ) : (
                <Money cents={remain} />
              );
            },
          },
          {
            title: '签订日期',
            dataIndex: 'signDate',
            width: 120,
            render: (v: string | null) => <span className="ilc-mono">{v ?? '-'}</span>,
          },
          { title: '结算阶段', dataIndex: 'settlementStage', width: 110, render: (v: string | null) => v ?? '-' },
          {
            title: '约定品类',
            dataIndex: 'categories',
            width: 200,
            render: (v: string[] | null) =>
              v?.length ? (
                <Space wrap size={4}>
                  {v.map((c) => (
                    <Tag key={c}>{c}</Tag>
                  ))}
                </Space>
              ) : (
                <Tooltip title="未维护约定品类时，合同比对无法判断发票开票项目是否与合同一致">
                  <Tag color="orange">未维护</Tag>
                </Tooltip>
              ),
          },
          {
            title: '状态',
            dataIndex: 'status',
            width: 100,
            render: (v: string) => <Tag color={v === '履行中' ? 'green' : 'default'}>{v}</Tag>,
          },
          {
            title: '操作',
            key: 'action',
            width: 140,
            fixed: 'right',
            render: (_v, record) => (
              <Space size={4}>
                <Button
                  type="link"
                  size="small"
                  disabled={!canWrite}
                  onClick={() => {
                    /*
                     * 只设状态，**不要在这里 form.setFieldsValue()**。
                     *
                     * Modal 带 destroyOnHidden，关闭时会卸载内部的 Form，
                     * 此刻 form 实例处于"未连接"状态；同步调用 setFieldsValue
                     * 会被直接丢弃，等 Form 重新挂载时又按 initialValues（空）初始化，
                     * 结果就是**编辑弹窗一片空白**——而标题却是对的（title 来自 state），
                     * 于是看起来像"数据没取到"，实际是填值的时机不对。
                     * 初值改由下面的 initialValues + key 交给 Form 自己处理。
                     */
                    setEditing(record);
                    // 带出已有附件（列表接口不返回，必须单独取详情）
                    void contractApi
                      .detail(record.id)
                      .then((d) => setUploaded(d.attachments ?? []))
                      .catch(() => setUploaded([]));
                  }}
                >
                  编辑
                </Button>
              </Space>
            ),
          },
        ]}
        dataSource={list.list}
        rowKey="id"
        loading={list.loading}
        error={list.error}
        isFallback={list.isFallback}
        fallbackReason={list.fallbackReason}
        onRetry={list.reload}
        pagination={list.pagination}
        onChange={list.handleTableChange}
        emptyText="暂无合同，点击右上角「新增合同」录入"
        toolbar={
          <Space wrap>
            <Input.Search
              placeholder="合同编号 / 名称 / 供应商"
              allowClear
              style={{ width: 260 }}
              onSearch={(value) => list.setQuery({ keyword: value })}
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              disabled={!canWrite}
              onClick={() => {
                setCreating(true);
                form.resetFields();
              }}
            >
              新增合同
            </Button>
          </Space>
        }
      />

      <Modal
        open={creating || editing !== null}
        title={editing ? `编辑合同 ${editing.contractNo}` : '新增合同'}
        onCancel={() => {
          setCreating(false);
          setEditing(null);
        }}
        onOk={() => void save()}
        confirmLoading={saving}
        destroyOnHidden
        width={editing ? 720 : 820}
      >
        <Form
          key={editing?.id ?? 'new'}
          form={form}
          layout="vertical"
          preserve={false}
          initialValues={formInitialValues}
        >
          {/*
            仅新建时显示上传区。
            编辑时合同已存在，走附件区的「上传附件」按钮，不必再挤进表单。
          */}
          {/*
            合同电子版收件：上传 → 自动提取 → 预填。

            逻辑抽在 components/ContractAttachmentIntake 里，与「合同台账」的
            「登记合同」共用一份——那段代码里有几条不明显的规则（只填未被碰过的
            字段、低置信度不自动填、两个名称候选都不自动填），抄两份迟早分叉。
          */}
          <Form.Item>
            <ContractAttachmentIntake
              ref={intakeRef}
              form={form}
              contractId={null}
              onAttachmentIdsChange={setTempAttachmentIds}
              onChanged={(att) => setUploaded((prev) => [att, ...prev])}
              showList={false}
            />
          </Form.Item>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                name="contractNo"
                label="合同编号"
                rules={[{ required: true, message: '请填写合同编号' }]}
                extra={
                  editing
                    ? '合同编号是发票关联合同的依据，创建后不可修改——改动它会让已关联的发票找不到合同'
                    : undefined
                }
              >
                {/*
                  编辑时禁用：后端的更新字段映射表里**没有 contractNo**，
                  改了这个值会被静默忽略，用户会以为改成功了、刷新后却发现没变。
                  与其留着这个"能改但不生效"的输入框，不如直接锁上并说明原因。
                */}
                <Input className="ilc-mono" placeholder="如 HT-2026-0007" disabled={editing !== null} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="name" label="合同名称" rules={[{ required: true, message: '请填写合同名称' }]}>
                <Input placeholder="如 钢材采购合同" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="supplierName" label="供应商名称" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="supplierTaxNo" label="供应商税号">
                <Input className="ilc-mono" placeholder="18 位统一社会信用代码" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="amountYuan" label="合同金额（元）" rules={[{ required: true }]}>
                <InputNumber<number>
                  style={{ width: '100%' }}
                  min={0}
                  precision={2}
                  step={1000}
                  formatter={(value) => `${value ?? ''}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={(value) => Number((value ?? '').replace(/,/g, ''))}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="settlementStage" label="结算阶段">
                <Select
                  allowClear
                  placeholder="如 进度款 30%"
                  options={['预付款', '进度款 30%', '进度款 60%', '验收款', '质保金'].map((v) => ({
                    value: v,
                    label: v,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              {/*
                签订日期。后端的 sign_date 列与 schema 一直支持它，
                但表单里从来没有这个输入框——识别到的日期无处可填，
                合同比对也就永远少一个可用维度。
              */}
              <Form.Item name="signDate" label="签订日期">
                <DatePicker style={{ width: '100%' }} placeholder="选择签订日期" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="categories"
                label="约定品类（顿号分隔）"
                extra="用于判断发票开票项目是否在合同约定的品类范围内；留空则合同比对无法判断品类"
              >
                <Input placeholder="如 钢材、水泥、砂石料" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="remark" label="备注">
                <Input.TextArea rows={2} maxLength={200} showCount />
              </Form.Item>
            </Col>
          </Row>
        </Form>

        {/*
          附件区。
          新建时列出**本次上传**的附件（TEMP 态，保存合同时一并绑定）；
          编辑时列出该合同**已有的**附件，并可补传。
        */}
        {uploaded.length > 0 || editing ? (
          <div style={{ marginTop: 16 }}>
            <Space style={{ marginBottom: 8 }} size={8}>
              <Typography.Text strong>合同附件</Typography.Text>
              {editing ? (
                <ContractAttachmentIntake
                  ref={intakeRef}
                  form={form}
                  contractId={editing.id}
                  showList={false}
                  onChanged={(att) => {
                    setUploaded((prev) => [att, ...prev]);
                    list.reload();
                  }}
                  hint={null}
                />
              ) : null}
            </Space>

            {uploaded.length === 0 ? (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                暂无附件
              </Typography.Text>
            ) : (
              <Table<ContractAttachment>
                size="small"
                rowKey="id"
                pagination={false}
                dataSource={uploaded}
                locale={{ emptyText: '暂无附件' }}
                columns={[
                  { title: '文件名', dataIndex: 'fileName', ellipsis: true },
                  {
                    title: '提取状态',
                    width: 260,
                    render: (_v, r) => (
                      <AttachmentStatusTag
                        status={r.extractStatus}
                        impl={r.extractImpl}
                        mode={r.extractMode}
                        message={r.extractMessage}
                        missing={r.extractMissing}
                      />
                    ),
                  },
                  {
                    title: '表格',
                    width: 70,
                    render: (_v, r) => (r.tableCount > 0 ? `${r.tableCount} 个` : '-'),
                  },
                  {
                    title: '操作',
                    width: 190,
                    render: (_v, r) => (
                      <Space size={4}>
                        <Button
                          size="small"
                          type="link"
                          onClick={async () => {
                            /*
                             * 必须**单独取详情**，不能直接用列表里的这一行。
                             * 列表接口为了控制响应体积，序列化时不带
                             * tables / layoutPages / textContent（见后端
                             * toAttachmentDto 的 withContent 开关）。
                             * 直接把列表行当详情用，三个视图会全是空的——
                             * 而界面看起来一切正常，只是"没有数据"。
                             */
                            try {
                              setDetailAtt(await contractApi.attachmentDetail(r.id));
                            } catch {
                              feedback.error('无法加载附件详情');
                            }
                          }}
                        >
                          详情
                        </Button>
                        <Button
                          size="small"
                          type="link"
                          disabled={r.extractStatus === 'UNAVAILABLE'}
                          onClick={async () => {
                            try {
                              const { url } = await contractApi.attachmentUrl(r.id);
                              window.open(url, '_blank', 'noopener');
                            } catch {
                              feedback.error('无法获取原件链接');
                            }
                          }}
                        >
                          查看原件
                        </Button>
                      </Space>
                    ),
                  },
                ]}
              />
            )}
          </div>
        ) : null}
      </Modal>

      {/* 附件详情抽屉：宽表格放这里，不与弹窗抢层级 */}
      <Drawer
        open={detailAtt !== null}
        onClose={() => setDetailAtt(null)}
        width={900}
        title={`附件详情 · ${detailAtt?.fileName ?? ''}`}
        extra={
          <Space>
            <Button
              onClick={async () => {
                if (!detailAtt) return;
                try {
                  const { url } = await contractApi.attachmentUrl(detailAtt.id);
                  window.open(url, '_blank', 'noopener');
                } catch {
                  feedback.error('无法获取原件链接');
                }
              }}
            >
              查看原件
            </Button>
            <Button
              disabled={detailAtt?.extractStatus === 'UNAVAILABLE'}
              onClick={async () => {
                if (!detailAtt) return;
                try {
                  const updated = await contractApi.reparseAttachment(detailAtt.id);
                  setDetailAtt(updated);
                  setUploaded((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
                  // 重新提取后必须刷新面板，否则用户点了「重新提取」看不到任何变化
                  intakeRef.current?.refreshFrom(updated);
                  feedback.success('已重新提取');
                } catch {
                  feedback.error('重新提取失败');
                }
              }}
            >
              重新提取
            </Button>
          </Space>
        }
      >
        {detailAtt ? (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Descriptions column={2} size="small" bordered>
              <Descriptions.Item label="文件类型">{detailAtt.fileKind}</Descriptions.Item>
              <Descriptions.Item label="大小">
                {(detailAtt.fileSize / 1024).toFixed(0)} KB
              </Descriptions.Item>
              <Descriptions.Item label="提取状态" span={2}>
                <AttachmentStatusTag
                  status={detailAtt.extractStatus}
                  impl={detailAtt.extractImpl}
                  mode={detailAtt.extractMode}
                  message={detailAtt.extractMessage}
                  missing={detailAtt.extractMissing}
                />
              </Descriptions.Item>
              {detailAtt.extractMessage ? (
                <Descriptions.Item label="说明" span={2}>
                  {detailAtt.extractMessage}
                </Descriptions.Item>
              ) : null}
              <Descriptions.Item label="页数">{detailAtt.pageCount ?? '-'}</Descriptions.Item>
              <Descriptions.Item label="表格">{detailAtt.tableCount} 个</Descriptions.Item>
            </Descriptions>

            {/*
              三种视图并列，各自标注适用场景：
                版面还原 —— 不做结构推断，忠实还原位置（扫描件唯一可选中/复制的视图）
                提取的表格 —— 有结构但可能错，必须提示以原件为准
                合同正文 —— 纯文本，便于搜索与复制
            */}
            <Tabs
              items={[
                {
                  key: 'layout',
                  label: '版面还原',
                  children: <LayoutPreview pages={detailAtt.layoutPages} />,
                },
                {
                  key: 'tables',
                  label: `提取的表格${detailAtt.tableCount > 0 ? `（${detailAtt.tableCount}）` : ''}`,
                  children: <TablePreview tables={detailAtt.tables} />,
                },
                {
                  key: 'text',
                  label: '合同正文',
                  children: detailAtt.textContent ? (
                    <div>
                      {detailAtt.textTruncated ? (
                        <Tag color="orange" style={{ marginBottom: 8 }}>
                          已截断（全文过长）
                        </Tag>
                      ) : null}
                      <Typography.Paragraph
                        style={{
                          whiteSpace: 'pre-wrap',
                          maxHeight: 520,
                          overflow: 'auto',
                          background: 'rgba(0,0,0,0.02)',
                          padding: 12,
                          fontSize: 12,
                        }}
                      >
                        {detailAtt.textContent}
                      </Typography.Paragraph>
                    </div>
                  ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="无正文" />
                  ),
                },
              ]}
            />
          </Space>
        ) : null}
      </Drawer>
    </>
  );
}

/** 事后抽查 —— 自动化跑完之后，人工回头验一遍 */
function SamplingTab(): React.ReactElement {
  const navigate = useNavigate();
  const [sampleSize, setSampleSize] = useState(5);
  const [sampleSeed, setSampleSeed] = useState(0);
  const [marked, setMarked] = useState<Record<string, string>>({});
  const [noteTarget, setNoteTarget] = useState<InvoiceListItem | null>(null);
  const [note, setNote] = useState('');

  const list = usePagedList<InvoiceListItem, SampleQuery>({
    loader: (q) => invoiceApi.list({ ...q, status: InvoiceStatus.BOOKED }),
    fallback: (q) => mockInvoiceList({ ...q, status: InvoiceStatus.BOOKED }),
    initialQuery: { page: 1, pageSize: 20 },
  });

  /** 按抽样数量随机取——同一批数据每次「重新抽样」结果不同，避免只抽查固定几张 */
  const sample = useMemo(() => {
    const pool = [...list.list];
    if (pool.length <= sampleSize) return pool;
    const picked: InvoiceListItem[] = [];
    const used = new Set<number>();
    while (picked.length < sampleSize && used.size < pool.length) {
      const index = Math.floor(Math.random() * pool.length);
      if (used.has(index)) continue;
      used.add(index);
      const item = pool[index];
      if (item) picked.push(item);
    }
    return picked;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list.list, sampleSize, sampleSeed]);

  const archives = useResource(() => archiveApi.list({ page: 1, pageSize: 20 }), {
    fallback: () => mockArchiveList({ page: 1, pageSize: 20 }),
  });

  const markAbnormal = (invoice: InvoiceListItem, reason: string): void => {
    setMarked((prev) => ({ ...prev, [invoice.id]: reason }));
    setNoteTarget(null);
    setNote('');
    feedback.warning(`已标记异常：${invoice.bizNo}，请在校验报告中补充说明后交财务负责人`);
  };

  return (
    <>
      <Alert
        type="info"
        showIcon
        icon={<AuditOutlined />}
        style={{ marginBottom: 12 }}
        message="事后抽查的目的不是抓错，而是验证「校验规则 + 人工判断」是否可靠"
        description="抽查对象是已记账 / 已归档的发票：重点看校验报告是否完整、外部校验不可用时的人工接管说明是否可追溯、高风险发票的特例放行依据是否充分。发现规则漏判请在页面下方记录，由技术实施组调整阈值。"
      />

      <Row gutter={[16, 16]} style={{ marginBottom: 12 }}>
        <Col xs={24} lg={10}>
          <Card size="small" title="抽样设置">
            <Space direction="vertical" style={{ width: '100%' }}>
              <Space>
                <Typography.Text>抽样数量</Typography.Text>
                <InputNumber min={1} max={50} value={sampleSize} onChange={(v) => setSampleSize(Number(v ?? 1))} />
                <Button
                  type="primary"
                  icon={<FileSearchOutlined />}
                  onClick={() => setSampleSeed((v) => v + 1)}
                  loading={list.loading}
                >
                  重新抽样
                </Button>
              </Space>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                从当前筛选结果（已记账）中随机抽取 {sampleSize} 张。如需按供应商或金额区间抽查，
                可先在下方列表用关键词与风险等级筛选后再抽样。
              </Typography.Text>
            </Space>
          </Card>
        </Col>
        <Col xs={24} lg={14}>
          <Card size="small" title="抽查结论汇总">
            <Space size={24} wrap>
              <Statistic title="本次抽中" value={sample.length} suffix="张" />
              <Statistic
                title="标记异常"
                value={Object.keys(marked).length}
                suffix="张"
                valueStyle={{ color: Object.keys(marked).length > 0 ? '#cf1322' : undefined }}
                prefix={<WarningOutlined />}
              />
              <Statistic
                title="已归档可追溯"
                value={archives.data?.total ?? 0}
                suffix="张"
                valueStyle={{ color: '#389e0d' }}
                prefix={<SafetyCertificateOutlined />}
              />
            </Space>
          </Card>
        </Col>
      </Row>

      <Card
        size="small"
        title={`抽样结果（${sample.length} 张）`}
        extra={
          <Space>
            <Button
              size="small"
              onClick={() =>
                exportCsv(
                  '事后抽查结果',
                  [
                    { title: '内部单号', dataIndex: 'bizNo' },
                    { title: '发票号码', dataIndex: 'invoiceNo' },
                    { title: '销方名称', dataIndex: 'sellerName' },
                    { title: '价税合计(元)', value: (r: InvoiceListItem) => formatCents(r.totalCents ?? 0) },
                    { title: '风险等级', dataIndex: 'riskLevel' },
                    { title: '状态', dataIndex: 'invoiceStatus' },
                  ],
                  sample,
                )
              }
            >
              导出抽查清单
            </Button>
            <Button size="small" onClick={() => setSampleSeed((v) => v + 1)}>
              换一批
            </Button>
          </Space>
        }
      >
        <PageState loading={list.loading} error={list.error} onRetry={list.reload} skeleton rows={4}>
          <Table<InvoiceListItem>
            size="small"
            rowKey="id"
            pagination={false}
            dataSource={sample}
            locale={{ emptyText: '暂无可抽查的已记账发票（需发票先完成 T+ 记账）' }}
            scroll={{ x: 'max-content' }}
            columns={[
              {
                title: '内部单号',
                dataIndex: 'bizNo',
                width: 160,
                render: (v: string, record) => (
                  <a className="ilc-mono" style={{ fontSize: 12 }} onClick={() => navigate(`/business/invoices/${record.id}`)}>
                    {v}
                  </a>
                ),
              },
              {
                title: '发票号码',
                dataIndex: 'invoiceNo',
                width: 170,
                render: (v: string) => (
                  <span className="ilc-mono" style={{ fontSize: 12 }}>
                    {v}
                  </span>
                ),
              },
              { title: '销方名称', dataIndex: 'sellerName', width: 200, ellipsis: true, render: (v: string | null) => v ?? '-' },
              {
                title: '价税合计',
                dataIndex: 'totalCents',
                width: 140,
                align: 'right',
                render: (v: number | null) => <Money cents={v} />,
              },
              {
                title: '风险',
                dataIndex: 'riskLevel',
                width: 110,
                render: (v: RiskLevel) => <RiskTag level={v} />,
              },
              {
                title: '状态',
                dataIndex: 'invoiceStatus',
                width: 110,
                render: (v: InvoiceListItem['invoiceStatus']) => <InvoiceStatusTag status={v} />,
              },
              {
                title: '抽查结论',
                key: 'conclusion',
                width: 220,
                render: (_v, record) =>
                  marked[record.id] ? (
                    <Space direction="vertical" size={2}>
                      <Tag color="red">已标记异常</Tag>
                      <Typography.Text style={{ fontSize: 12 }}>{marked[record.id]}</Typography.Text>
                    </Space>
                  ) : (
                    <Tag color="green">未发现问题</Tag>
                  ),
              },
              {
                title: '操作',
                key: 'action',
                width: 160,
                fixed: 'right',
                render: (_v, record) => (
                  <Space size={4}>
                    <Button size="small" type="link" onClick={() => navigate(`/business/invoices/${record.id}/report`)}>
                      看报告
                    </Button>
                    <Button
                      size="small"
                      type="link"
                      danger
                      onClick={() => {
                        setNoteTarget(record);
                        setNote('');
                      }}
                    >
                      标记异常
                    </Button>
                  </Space>
                ),
              },
            ]}
          />
        </PageState>
      </Card>

      <Card size="small" title="抽查记录" style={{ marginTop: 16 }}>
        {Object.keys(marked).length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="本次抽查尚未标记异常" />
        ) : (
          <Table
            size="small"
            rowKey="id"
            pagination={false}
            dataSource={Object.entries(marked).map(([id, reason]) => ({
              id,
              bizNo: sample.find((s) => s.id === id)?.bizNo ?? id,
              reason,
            }))}
            columns={[
              { title: '内部单号', dataIndex: 'bizNo', width: 180 },
              { title: '异常说明', dataIndex: 'reason' },
              {
                title: '操作',
                key: 'action',
                width: 120,
                render: (_v, record) => (
                  <Space size={4}>
                    <Button size="small" type="link" onClick={() => navigate(`/business/invoices/${record.id}`)}>
                      去处理
                    </Button>
                    <Button
                      size="small"
                      type="link"
                      onClick={() =>
                        setMarked((prev) => {
                          const next = { ...prev };
                          delete next[record.id];
                          return next;
                        })
                      }
                    >
                      撤销
                    </Button>
                  </Space>
                ),
              },
            ]}
          />
        )}
        <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginTop: 8, marginBottom: 0 }}>
          抽查记录目前保存在本页会话中；后端「抽查与整改」接口就绪后会写入数据库并生成整改任务。
        </Typography.Paragraph>
      </Card>

      <Modal
        open={noteTarget !== null}
        title={`标记异常 —— ${noteTarget?.bizNo ?? ''}`}
        onCancel={() => setNoteTarget(null)}
        onOk={() => {
          if (!noteTarget) return;
          if (note.trim().length < 8) {
            feedback.warning('异常说明至少 8 个字，便于后续整改与复盘');
            return;
          }
          markAbnormal(noteTarget, note.trim());
        }}
        okText="确认标记"
        destroyOnHidden
      >
        <Descriptions column={1} size="small" style={{ marginBottom: 12 }}>
          <Descriptions.Item label="发票号码">
            <span className="ilc-mono">{noteTarget?.invoiceNo}</span>
          </Descriptions.Item>
          <Descriptions.Item label="销方">{noteTarget?.sellerName ?? '-'}</Descriptions.Item>
          <Descriptions.Item label="价税合计">
            <Money cents={noteTarget?.totalCents} />
          </Descriptions.Item>
          <Descriptions.Item label="风险等级">
            <RiskTag level={noteTarget?.riskLevel} />
          </Descriptions.Item>
          <Descriptions.Item label="发票状态">
            {noteTarget ? INVOICE_STATUS_LABELS[noteTarget.invoiceStatus] : '-'}
          </Descriptions.Item>
        </Descriptions>
        <Input.TextArea
          rows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="如：发票开票项目与合同约定品类不符，但校验报告未标记为风险项，建议调整合同比对规则"
        />
        <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginTop: 8, marginBottom: 0 }}>
          异常说明将用于复盘校验规则与人工判断质量，不会自动改变发票状态（发票已记账，状态变更需走财务流程）。
        </Typography.Paragraph>
      </Modal>
    </>
  );
}

export default function LegalWorkbench(): React.ReactElement {
  const can = useAuthStore((s) => s.can);
  const navigate = useNavigate();
  const list = usePagedList<InvoiceListItem, SampleQuery>({
    loader: (q) => invoiceApi.list(q),
    fallback: (q) => mockInvoiceList(q),
    initialQuery: { page: 1, pageSize: 20 },
  });

  const riskRows = list.list.filter((r) => r.riskLevel === RiskLevel.HIGH || r.riskLevel === RiskLevel.MEDIUM);

  return (
    <div className="ilc-page">
      <PageHeader
        title="合同与抽查"
        description="合同台账维护、高风险发票复核、已记账发票的事后抽查。法务视角关注「业务真实性」与「凭据可追溯」。"
        extra={
          <Space>
            <Tag color="blue">可写合同：{can(PermissionCode.CONTRACT_WRITE) ? '是' : '否（只读）'}</Tag>
            <Tag color="blue">可导出档案：{can(PermissionCode.ARCHIVE_EXPORT) ? '是' : '否'}</Tag>
          </Space>
        }
      />

      <Tabs
        defaultActiveKey="contracts"
        items={[
          {
            key: 'contracts',
            label: '合同台账',
            children: <ContractTab />,
          },
          {
            key: 'sampling',
            label: '事后抽查',
            children: <SamplingTab />,
          },
          {
            key: 'risk',
            label: (
              <Space size={4}>
                高风险与中风险发票
                {riskRows.length > 0 ? <Tag color="red">{riskRows.length}</Tag> : null}
              </Space>
            ),
            children: (
              <>
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginBottom: 12 }}
                  message="法务关注的中风险主要是「超经营范围」与「合同不一致」"
                  description="按业务口径，这两类风险不做强制拦截（业务真实性由财务与业务部门判断），但需要有人复核并留痕：超经营范围看是否属于偶发兼营，合同不一致看是否存在补充协议。"
                />
                <DataTable<InvoiceListItem>
                  storageKey="legal-risk-invoices"
                  exportFileName="风险发票复核"
                  exportColumns={buildInvoiceExportColumns()}
                  columns={buildInvoiceColumns({
                    showHandler: true,
                    actions: (record) => (
                      <Button
                        type="link"
                        size="small"
                        onClick={() => navigate(`/business/invoices/${record.id}`)}
                      >
                        详情
                      </Button>
                    ),
                  })}
                  dataSource={riskRows}
                  rowKey="id"
                  loading={list.loading}
                  error={list.error}
                  isFallback={list.isFallback}
                  fallbackReason={list.fallbackReason}
                  onRetry={list.reload}
                  pagination={false}
                  emptyText="当前页没有中高风险发票"
                  toolbar={
                    <Space wrap>
                      <Select
                        placeholder="风险等级"
                        allowClear
                        style={{ width: 130 }}
                        value={list.query.riskLevel}
                        onChange={(value) => list.setQuery({ riskLevel: value })}
                        options={Object.entries(RISK_LEVEL_LABELS).map(([value, label]) => ({ value, label }))}
                      />
                      <Input.Search
                        placeholder="销方名称 / 发票号码"
                        allowClear
                        style={{ width: 240 }}
                        onSearch={(value) => list.setQuery({ keyword: value })}
                      />
                    </Space>
                  }
                />
              </>
            ),
          },
        ]}
      />
    </div>
  );
}
